import { useEffect, useRef } from 'react';
import { LoggingService } from '@/src/utils/logging-service.utils';

export interface UsePollingParams {
  /** Whether polling should run. */
  enabled: boolean;
  /** Poll interval in milliseconds. */
  intervalMs: number;
  /** Loader invoked on each tick. */
  load: () => void | Promise<unknown>;
  /** Label for failure logging. */
  logLabel?: string;
}

/** Generic interval poller with an in-flight guard - slow loads don't stack overlapping calls. */
export const usePolling = ({ enabled, intervalMs, load, logLabel = 'usePolling' }: UsePollingParams): void => {
  // Keep the latest `load` off the deps so a new callback identity doesn't recreate the interval.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Closure-local guard: each subscription owns its own in-flight flag and a stale pending load
    // can't release a newer subscription's guard.
    let inFlight = false;

    const tick = async () => {
      if (inFlight) {
        return;
      }
      inFlight = true;
      try {
        await loadRef.current();
      } catch (e) {
        LoggingService.debug(`${logLabel}: load failed`, e);
      } finally {
        inFlight = false;
      }
    };

    const interval = setInterval(() => void tick(), intervalMs);
    return () => {
      clearInterval(interval);
    };
  }, [enabled, intervalMs]);
};
